import { AnimatedLock } from 'assets/icons/AnimatedLock';
import cn from 'classnames';
import { motion } from 'framer-motion';
import React, { useMemo } from 'react';

type ProgressType = 'amount' | 'bets';

interface BaseProgressBarProps {
	progressType: ProgressType;
	current: number;
	total: number;
	label: string;
	className?: string;
	transitionSettings?: { type: string; stiffness: number; damping: number };
	onComplete?: () => void;
}

const defaultTransitionSettings = {
	type: 'spring',
	stiffness: 300,
	damping: 25,
};

const TOTAL_ANIMATION_DURATION = 1.2;
const SEGMENT_ANIMATION_DURATION = 0.3;

export const DSProgressBar: React.FC<BaseProgressBarProps> = ({
	progressType,
	current,
	total,
	label,
	className,
	transitionSettings = defaultTransitionSettings,
	onComplete,
}) => {
	const clampedCurrent = Math.min(Math.max(current, 0), total);
	const percent = useMemo(() => {
		if (!total) return 0;
		return (clampedCurrent / total) * 100;
	}, [clampedCurrent, total]);
	const isComplete = current === total;

	if (!total) return null;

	const animationDelayStart = TOTAL_ANIMATION_DURATION;
	const completeIconDelay =
		progressType === 'amount'
			? TOTAL_ANIMATION_DURATION
			: animationDelayStart + Math.max(0, current - 1) * SEGMENT_ANIMATION_DURATION + SEGMENT_ANIMATION_DURATION;

	const completeIcon = (
		<motion.div
			key={isComplete ? 'complete' : 'incomplete'}
			className="h-6 w-6 rounded-full border-[2px] -left-[2px] shrink-0 relative flex items-center justify-center border-gray-200 dark:border-dark-400"
			initial={true}
			animate={{
				scale: isComplete ? [1, 1.1, 1] : 1,
				boxShadow: isComplete
					? ['0 0 0px rgba(0,0,0,0)', '0 0 4px rgba(0, 255, 100, 0.4)', '0 0 0px rgba(0,0,0,0)']
					: '0 0 0px rgba(0,0,0,0)',
				backgroundColor: isComplete ? '#0cbf50' : 'transparent',
				borderColor: isComplete ? '#0cbf50' : '',
			}}
			transition={{
				scale: {
					duration: 0.4,
					times: [0, 0.4, 1],
					ease: 'easeInOut',
					delay: completeIconDelay,
				},
				backgroundColor: { duration: 0.3, delay: completeIconDelay },
				borderColor: { duration: 0.3, delay: completeIconDelay },
				boxShadow: { duration: 0.4, delay: completeIconDelay },
			}}
			onAnimationComplete={() => {
				if (isComplete && onComplete) {
					onComplete();
				}
			}}
		>
			<AnimatedLock delay={completeIconDelay} isComplete={isComplete} />
		</motion.div>
	);
	return (
		<div className={className}>
			<div className="flex items-center gap-[6px] justify-between h-6">
				<div
					className={cn('shrink-0 text-paragraph-xs', isComplete ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-gray-100')}
				>
					{label}
				</div>

				{progressType === 'amount' ? (
					<div className="flex items-center justify-between w-full">
						<div className="relative w-full">
							<div className="border-gray-200 dark:border-dark-400 border-[2px] rounded-tl-16 rounded-bl-16 w-full h-1.5">
								<motion.div
									className="bg-success-500 z-2 absolute top-0 left-0 h-1.5 rounded-tl-16 rounded-bl-16"
									initial={{ width: 0 }}
									animate={{ width: `${percent}%` }}
									transition={{
										...transitionSettings,
										duration: SEGMENT_ANIMATION_DURATION,
										delay: TOTAL_ANIMATION_DURATION - SEGMENT_ANIMATION_DURATION,
									}}
								/>
							</div>
						</div>

						{completeIcon}
					</div>
				) : (
					<div className="relative flex w-full items-center gap-0 h-1.5">
						<div className="absolute top-0 left-0 flex w-[calc(100%-24px)] h-full z-0">
							{Array.from({ length: total }).map((_, index) => {
								const isFirst = index === 0;
								const isLast = index === total - 1;
								const isFilled = index < current;

								return (
									<div
										key={`bg-${index}`}
										className={cn('flex-1 border-y-2 border-r-2 border-l-2 border-gray-200 dark:border-dark-400', {
											'rounded-tl-16 rounded-bl-16': isFirst,
											'rounded-tr-16 rounded-br-16': isLast,
											'border-transparent': isFilled,
											'-ml-0.5': !isFilled,
										})}
									/>
								);
							})}
						</div>

						<div className="absolute top-0 left-0 flex w-[calc(100%-24px)] h-full z-10">
							{Array.from({ length: total }).map((_, index) => {
								const isFilled = index < current;
								const isFirst = index === 0;
								const isLast = index === total - 1;
								const segmentDelay = animationDelayStart + index * SEGMENT_ANIMATION_DURATION;

								return (
									<motion.div
										key={`overlay-${index}`}
										className={cn('flex-1 bg-success-500 border-r-2 border-white dark:border-dark-200', {
											'rounded-tl-16 rounded-bl-16': isFirst,
											'rounded-tr-16 rounded-br-16 border-l-0': isLast,
										})}
										initial={{ opacity: 0 }}
										animate={{ opacity: isFilled ? 1 : 0 }}
										transition={{
											duration: SEGMENT_ANIMATION_DURATION,
											delay: segmentDelay,
											ease: 'easeInOut',
										}}
									/>
								);
							})}
						</div>

						<div className="relative z-20 ml-auto">{completeIcon}</div>
					</div>
				)}
			</div>
		</div>
	);
};
